import { useState } from 'react';
import { Button, Space, Typography, notification, DatePicker } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { usePostExportLedgerMutation } from '../../redux/api/auditing';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = 'America/Chicago';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const LedgerExports = () => {
    const [exportLedger, { isLoading }] = usePostExportLedgerMutation();
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

    const handleExport = async (ledgerType: string, label: string) => {
        try {
            const payload: { ledger_type: string; start_date?: string; end_date?: string } = {
                ledger_type: ledgerType,
            };

            if (dateRange && dateRange[0] && dateRange[1]) {
                payload.start_date = dateRange[0].tz(TIMEZONE).format('YYYY-MM-DD');
                payload.end_date = dateRange[1].tz(TIMEZONE).format('YYYY-MM-DD');
            }

            const response = await exportLedger(payload).unwrap();

            if (response.url) {
                window.open(response.url, '_blank');
            }

            notification.success({
                message: 'Export Started',
                description: `Export for ${label} has been triggered. The Google Sheet should open shortly.`,
            });
        } catch (err) {
            notification.error({
                message: 'Export Failed',
                description: 'An error occurred while triggering the export.',
            });
        }
    };

    const buttons = [
        { label: 'Manual Inventory Value Association Ledger', type: 'manual_inventory' },
        { label: 'Job Material Cost Ledger', type: 'job_material_cost' },
        { label: 'Material Correction Cost Ledger', type: 'material_correction' },
        { label: 'Material Inventory Ledger', type: 'material_inventory' },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Ledgers</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                Trigger Google Sheets exports for different ledger types. Select a date range to filter the export.
            </Text>

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Space>
                    <RangePicker
                        onChange={(dates) => setDateRange(dates)}
                        value={dateRange}
                        disabledDate={(current) => current && current.isAfter(dayjs().tz(TIMEZONE), 'day')}
                        style={{ width: 300 }}
                    />
                    <Button
                        size="small"
                        onClick={() => setDateRange([dayjs().tz(TIMEZONE).startOf('day'), dayjs().tz(TIMEZONE).endOf('day')])}
                    >
                        Today
                    </Button>
                </Space>

                <Space wrap>
                    {buttons.map((btn) => (
                        <Button
                            key={btn.type}
                            type="primary"
                            icon={<DownloadOutlined />}
                            size="large"
                            loading={isLoading}
                            onClick={() => handleExport(btn.type, btn.label)}
                        >
                            {btn.label}
                        </Button>
                    ))}
                </Space>
            </Space>
        </div>
    );
};

export default LedgerExports;
